/**
 * useOfflineQueue — Cola offline para operaciones que fallan por falta de red.
 *
 * Persiste operaciones pendientes en localStorage y las reintenta
 * automáticamente con backoff exponencial cuando vuelve la conexión.
 *
 * Originalmente diseñado para pedidos de camarero: si el WiFi cae mientras
 * el camarero está tomando un pedido, el pedido se guarda localmente y se
 * envía cuando vuelve la conexión.
 *
 * Ampliado (mayo 2026) para soportar también **cierres de caja**: el momento
 * más sensible del día (fiscal + reconciliación). Si el router del bar se
 * apaga justo al cerrar, el cierre queda encolado y se reintenta solo.
 *
 * Diseño: discriminated union sobre `tipo` ('pedido' | 'cierre') que comparte
 * toda la infraestructura (listeners, backoff, retries, persistencia).
 */

/**
 * Asignación de un item del carrito a comensales anónimos numerados.
 * Ticket #22a. Se persiste en la cola para que al replayear tras recuperar
 * red se propaguen las 5 columnas split a items_pedido.
 */
export type AsignacionComensalQueue =
  | { tipo: 'individual'; comensalNumero: number }
  | { tipo: 'compartido'; numeros: number[] }

/** Base común a todas las operaciones encoladas */
interface OperacionBase {
  id: string
  timestamp: number
  retries: number
  lastError?: string
  /** Marca permanente cuando se agotan retries no-red */
  failedPermanently?: boolean
}

export interface PedidoPendiente extends OperacionBase {
  tipo: 'pedido'
  restauranteId: string
  mesa: { id: string; numero: string }
  sesionId: string | null
  empleadoId: string | null
  carrito: Array<{
    plato_id: string
    nombre: string
    precio: number
    cantidad: number
    notas: string
    variante_seleccionada?: any
    modificaciones?: any[]
    estacion_preparacion?: string
    /** Turno/ronda de salida a cocina: 1=bebidas (por defecto), 2=comida, etc. */
    turno_salida?: number
    /** Ticket #22a */
    asignacion?: AsignacionComensalQueue
  }>
  notasCocina: string
  comensales: number
  totalCarrito: number
}

/** Payload del UPDATE de cierre de caja (la única operación BD que hace `cerrarCaja`) */
export interface CierrePendiente extends OperacionBase {
  tipo: 'cierre'
  restauranteId: string
  cierreId: string
  /** Etiqueta humana para UI (ej: "Caja del 17-05") */
  etiqueta: string
  payload: {
    hora_cierre: string
    efectivo_esperado: number
    efectivo_real: number
    descuadre: number
    total_ventas: number
    total_propinas: number
    total_gastos: number
    num_pedidos: number
    desglose_metodos_pago: Record<string, number>
    estado: 'cerrada'
    observaciones?: string | null
    tipo_iva: number
    base_imponible: number
    cuota_iva: number
    primera_factura: string | null
    ultima_factura: string | null
    num_facturas: number
  }
}

export type QueuedOperation = PedidoPendiente | CierrePendiente

// Paleta para comensales auto-creados (Ticket #22a). Sincronizado con
// useCarritoCompartido y usePedidoCamarero.
const QUEUE_COLORES = [
  '#EF4444', '#3B82F6', '#10B981', '#F59E0B',
  '#8B5CF6', '#EC4899', '#06B6D4', '#F97316',
]
const QUEUE_AVATARES = ['😊', '😎', '🤠', '🧑‍🍳', '🍕', '🍺', '🍷', '☕']

const QUEUE_KEY = 'cp_offline_queue'
const MAX_RETRIES = 10
const BASE_DELAY_MS = 2000 // 2s, 4s, 8s, 16s...
const MAX_DELAY_MS = 60000

// ─────────────────────────────────────────────────────────────────────
// Helpers puros (exportados para tests sin mocks)
// ─────────────────────────────────────────────────────────────────────

/**
 * Backoff exponencial: 2s, 4s, 8s, 16s, ... acotado a 60s.
 * `retries` es el contador YA incrementado (1 = primer reintento).
 */
export function calcularDelayBackoff(retries: number): number {
  if (retries <= 0) return BASE_DELAY_MS
  return Math.min(BASE_DELAY_MS * Math.pow(2, retries - 1), MAX_DELAY_MS)
}

/** Si tras incrementar `retries` superamos el máximo, la op se considera fallida permanente. */
export function esFalloPermanente(retries: number): boolean {
  return retries >= MAX_RETRIES
}

/**
 * Decide qué hacer ante un intento de cierre.
 *
 * - online + sin error  → 'commit' (el caller usa el resultado directo)
 * - offline al iniciar  → 'encolar' (no intentar, ahorrar latencia)
 * - error de red        → 'encolar' (se reintentará automáticamente)
 * - error no-red        → 'fail'    (validación / RLS — el caller debe avisar al humano)
 */
export type AccionCierre = 'commit' | 'encolar' | 'fail'

export function decidirAccionCierre(input: {
  online: boolean
  intentoOK: boolean
  error: any
}): AccionCierre {
  if (!input.online) return 'encolar'
  if (input.intentoOK) return 'commit'
  if (isNetworkError(input.error)) return 'encolar'
  return 'fail'
}

// ─────────────────────────────────────────────────────────────────────
// Estado global compartido entre todas las instancias del composable
// ─────────────────────────────────────────────────────────────────────

const isOnline = ref(true)
const pendingOps = ref<QueuedOperation[]>([])
const isSyncing = ref(false)
const lastSyncError = ref<string | null>(null)

let _processingTimer: ReturnType<typeof setTimeout> | null = null
let _initialized = false

/**
 * Migración de items pre-union: en versiones anteriores la cola solo guardaba
 * `PedidoPendiente` sin campo `tipo`. Los promovemos a `tipo: 'pedido'`.
 */
function migrarItemLegacy(raw: any): QueuedOperation | null {
  if (!raw || typeof raw !== 'object') return null
  if (raw.tipo === 'pedido' || raw.tipo === 'cierre') return raw as QueuedOperation
  // Sin tipo + tiene `mesa` y `carrito` → era un pedido viejo
  if (raw.mesa && raw.carrito) {
    return { ...raw, tipo: 'pedido' } as PedidoPendiente
  }
  return null
}

function loadQueue(): QueuedOperation[] {
  if (import.meta.server) return []
  try {
    const raw = localStorage.getItem(QUEUE_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return []
    return arr.map(migrarItemLegacy).filter((x): x is QueuedOperation => x !== null)
  } catch {
    return []
  }
}

function saveQueue(queue: QueuedOperation[]) {
  if (import.meta.server) return
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue))
  } catch {
    // localStorage full — unlikely but handle gracefully
  }
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export const useOfflineQueue = () => {
  const supabase = useSupabaseClient()

  // Inicializar listeners una sola vez
  if (!_initialized && import.meta.client) {
    _initialized = true
    isOnline.value = navigator.onLine
    pendingOps.value = loadQueue()

    window.addEventListener('online', () => {
      isOnline.value = true
      lastSyncError.value = null
      processQueue()
    })

    window.addEventListener('offline', () => {
      isOnline.value = false
      if (_processingTimer) {
        clearTimeout(_processingTimer)
        _processingTimer = null
      }
    })

    // Si hay operaciones pendientes y estamos online, procesarlas al arrancar
    if (isOnline.value && pendingOps.value.length > 0) {
      setTimeout(() => processQueue(), 1000)
    }
  }

  /**
   * Encolar un pedido para envío. Se intenta enviar inmediatamente si hay red.
   * Si no hay red, se guarda en localStorage y se envía cuando vuelva.
   */
  const enqueueOrder = (
    order: Omit<PedidoPendiente, 'id' | 'timestamp' | 'retries' | 'tipo'>,
  ) => {
    const pending: PedidoPendiente = {
      ...order,
      tipo: 'pedido',
      id: generateId(),
      timestamp: Date.now(),
      retries: 0,
    }

    pendingOps.value.push(pending)
    saveQueue(pendingOps.value)

    if (isOnline.value) {
      processQueue()
    }

    return pending.id
  }

  /**
   * Encolar un cierre de caja. Igual que `enqueueOrder` pero para la operación
   * de cierre (UPDATE de `cierres_caja`). El caller decide cuándo encolar:
   * típicamente tras un fallo de red en `cerrarCaja` o si arranca offline.
   */
  const enqueueCierre = (
    cierre: Omit<CierrePendiente, 'id' | 'timestamp' | 'retries' | 'tipo'>,
  ) => {
    const pending: CierrePendiente = {
      ...cierre,
      tipo: 'cierre',
      id: generateId(),
      timestamp: Date.now(),
      retries: 0,
    }

    pendingOps.value.push(pending)
    saveQueue(pendingOps.value)

    if (isOnline.value) {
      processQueue()
    }

    return pending.id
  }

  /**
   * Procesar la cola: enviar operaciones pendientes una a una, en orden.
   */
  const processQueue = async () => {
    if (isSyncing.value || !isOnline.value || pendingOps.value.length === 0) return

    isSyncing.value = true
    lastSyncError.value = null

    // Copiar el array para iterar seguro
    const queue = [...pendingOps.value]

    for (const op of queue) {
      if (!isOnline.value) break // Se fue la red durante el procesamiento
      if (op.failedPermanently) continue // No reintentar lo ya marcado como muerto

      try {
        if (op.tipo === 'pedido') {
          await sendOrderToSupabase(op)
        } else if (op.tipo === 'cierre') {
          await sendCierreToSupabase(op)
        }
        // Éxito: quitar de la cola
        pendingOps.value = pendingOps.value.filter(o => o.id !== op.id)
        saveQueue(pendingOps.value)
      } catch (e: any) {
        if (isNetworkError(e)) {
          // Error de red: programar reintento con backoff
          op.retries++
          op.lastError = 'Sin conexión'
          saveQueue(pendingOps.value)

          if (!esFalloPermanente(op.retries)) {
            const delay = calcularDelayBackoff(op.retries)
            _processingTimer = setTimeout(() => processQueue(), delay)
          } else {
            op.failedPermanently = true
            saveQueue(pendingOps.value)
            lastSyncError.value =
              op.tipo === 'pedido'
                ? `Pedido para mesa ${op.mesa.numero} falló después de ${MAX_RETRIES} intentos`
                : `Cierre "${op.etiqueta}" falló después de ${MAX_RETRIES} intentos`
          }
          break // No seguir procesando si no hay red
        }

        // Error no-red (RLS, validación, etc): marcar pero no bloquear la cola
        op.retries++
        op.lastError = e.message || 'Error al enviar'
        saveQueue(pendingOps.value)

        if (esFalloPermanente(op.retries)) {
          op.failedPermanently = true
          saveQueue(pendingOps.value)
          lastSyncError.value =
            op.tipo === 'pedido'
              ? `Pedido para mesa ${op.mesa.numero} falló después de ${MAX_RETRIES} intentos`
              : `Cierre "${op.etiqueta}" falló después de ${MAX_RETRIES} intentos`
        }
      }
    }

    isSyncing.value = false
  }

  /**
   * Aplicar el UPDATE de un cierre encolado. Si el `cierre_id` ya está cerrado
   * en BD (porque otro tab cerró primero), tratamos como éxito idempotente.
   */
  const sendCierreToSupabase = async (op: CierrePendiente) => {
    const { error: err } = await supabase
      .from('cierres_caja')
      .update(op.payload)
      .eq('id', op.cierreId)

    if (err) throw err
  }

  /**
   * Enviar un pedido a Supabase — replica la lógica de usePedidoCamarero.enviarPedido.
   * Ticket #22a: orden session-first + propagación de 5 columnas split.
   */
  const sendOrderToSupabase = async (order: PedidoPendiente) => {
    const origenPedido = order.sesionId ? 'asistido' : 'camarero'
    const numComensales = order.comensales || 1
    let sesionIdEfectivo: string | null = order.sesionId || null
    let sesionReciénCreada = false

    // 1. Asegurar sesión
    if (!sesionIdEfectivo) {
      const { data: nuevaSesion, error: errSes } = await supabase
        .from('sesiones_mesa')
        .insert({
          restaurante_id: order.restauranteId,
          mesa_id: order.mesa.id,
          estado: 'activa',
          comensales: numComensales,
          total_acumulado: 0,
          hora_inicio: new Date(order.timestamp).toISOString(),
          hora_ultimo_pedido: new Date().toISOString(),
        })
        .select('id')
        .single()
      if (errSes || !nuevaSesion) throw errSes || new Error('No se pudo crear la sesión (offline queue)')
      sesionIdEfectivo = (nuevaSesion as { id: string }).id
      sesionReciénCreada = true
    }

    // 2. Asegurar comensales_sesion si hay split activo (comensales >= 2)
    const comensalIdPorNumero = new Map<number, { id: string; nombre: string }>()
    if (numComensales >= 2 && sesionIdEfectivo) {
      const { data: existentes } = await supabase
        .from('comensales_sesion')
        .select('id, nombre, orden_llegada')
        .eq('sesion_id', sesionIdEfectivo)
        .order('orden_llegada')
      const existentesTyped = (existentes || []) as Array<{ id: string; nombre: string; orden_llegada: number }>
      for (const c of existentesTyped) {
        comensalIdPorNumero.set(c.orden_llegada, { id: c.id, nombre: c.nombre })
      }
      const aInsertar: any[] = []
      for (let n = 1; n <= numComensales; n++) {
        if (!comensalIdPorNumero.has(n)) {
          aInsertar.push({
            sesion_id: sesionIdEfectivo,
            nombre: `Comensal ${n}`,
            orden_llegada: n,
            color: QUEUE_COLORES[(n - 1) % QUEUE_COLORES.length],
            avatar: QUEUE_AVATARES[(n - 1) % QUEUE_AVATARES.length],
          })
        }
      }
      if (aInsertar.length > 0) {
        const { data: insertados, error: errIns } = await supabase
          .from('comensales_sesion')
          .insert(aInsertar)
          .select('id, nombre, orden_llegada')
        if (errIns) throw errIns
        const insertadosTyped = (insertados || []) as Array<{ id: string; nombre: string; orden_llegada: number }>
        for (const c of insertadosTyped) {
          comensalIdPorNumero.set(c.orden_llegada, { id: c.id, nombre: c.nombre })
        }
      }
    }

    // 3. Crear pedido con sesion_id correcto
    const { data: pedido, error: errPedido } = await supabase
      .from('pedidos')
      .insert({
        restaurante_id: order.restauranteId,
        mesa_id: order.mesa.id,
        sesion_id: sesionIdEfectivo,
        numero_mesa: order.mesa.numero,
        estado: 'pendiente',
        tipo_servicio: 'mesa',
        origen: origenPedido,
        empleado_id: order.empleadoId,
        subtotal: order.totalCarrito,
        total: order.totalCarrito,
        notas_cocina: order.notasCocina || null,
        comensales: numComensales,
      })
      .select()
      .single()

    if (errPedido) throw errPedido

    // 4. Crear items con columnas split propagadas
    const items = order.carrito.map((item) => {
      const base: any = {
        pedido_id: (pedido as any).id,
        plato_id: item.plato_id,
        nombre_plato: item.nombre,
        precio: item.precio * item.cantidad,
        precio_unitario: item.precio,
        cantidad: item.cantidad,
        notas: item.notas || null,
        variante_seleccionada: item.variante_seleccionada,
        modificaciones: item.modificaciones || [],
        estado: 'pendiente',
        estacion_preparacion: item.estacion_preparacion || null,
        // Ronda de salida a cocina, igual que el envío con conexión.
        turno_salida: item.turno_salida ?? 1,
      }
      const a = item.asignacion
      if (!a || numComensales < 2) return base
      if (a.tipo === 'individual') {
        const c = comensalIdPorNumero.get(a.comensalNumero)
        return {
          ...base,
          comensal_id: c?.id || null,
          nombre_comensal: c?.nombre || null,
          para_compartir: false,
          compartir_con: null,
          nombres_compartir: null,
        }
      }
      const uuids: string[] = []
      const nombres: string[] = []
      for (const n of a.numeros) {
        const c = comensalIdPorNumero.get(n)
        if (c) { uuids.push(c.id); nombres.push(c.nombre) }
      }
      return {
        ...base,
        comensal_id: null,
        nombre_comensal: null,
        para_compartir: true,
        compartir_con: uuids.length > 0 ? uuids : null,
        nombres_compartir: nombres.length > 0 ? nombres : null,
      }
    })

    const { error: errItems } = await supabase
      .from('items_pedido')
      .insert(items)

    if (errItems) {
      // Rollback: eliminar el pedido huérfano
      await supabase.from('pedidos').delete().eq('id', (pedido as any).id)
      throw errItems
    }

    // 5. Actualizar total_acumulado + ocupar mesa si sesión recién creada
    if (sesionReciénCreada) {
      await supabase
        .from('sesiones_mesa')
        .update({
          total_acumulado: order.totalCarrito,
          hora_ultimo_pedido: new Date().toISOString(),
        })
        .eq('id', sesionIdEfectivo!)
      await supabase
        .from('mesas')
        .update({ estado: 'ocupada', hora_ocupacion: new Date(order.timestamp).toISOString() })
        .eq('id', order.mesa.id)
    } else {
      const { data: pedidosSesion } = await supabase
        .from('pedidos')
        .select('total')
        .eq('sesion_id', sesionIdEfectivo!)
        .neq('estado', 'cancelado')
      const nuevoTotal = (pedidosSesion || []).reduce((acc: number, p: any) => acc + (p.total || 0), 0)
      const updateSesion: any = {
        total_acumulado: nuevoTotal,
        hora_ultimo_pedido: new Date().toISOString(),
      }
      if (numComensales > 0) updateSesion.comensales = numComensales
      await supabase
        .from('sesiones_mesa')
        .update(updateSesion)
        .eq('id', sesionIdEfectivo!)
    }
  }

  /**
   * Eliminar una operación de la cola (si el usuario decide descartarla)
   */
  const removeFromQueue = (opId: string) => {
    pendingOps.value = pendingOps.value.filter(o => o.id !== opId)
    saveQueue(pendingOps.value)
  }

  /**
   * Forzar reintento manual de la cola
   */
  const retryNow = () => {
    if (isOnline.value) {
      processQueue()
    }
  }

  const pendingCount = computed(() => pendingOps.value.length)

  // Vista filtrada solo de pedidos — retrocompat con UI existente
  const pendingOrders = computed(
    () => pendingOps.value.filter((o): o is PedidoPendiente => o.tipo === 'pedido'),
  )

  // Vista filtrada de cierres
  const pendingCierres = computed(
    () => pendingOps.value.filter((o): o is CierrePendiente => o.tipo === 'cierre'),
  )

  const pendingForMesa = (mesaId: string) =>
    computed(() =>
      pendingOps.value.filter(
        (o): o is PedidoPendiente => o.tipo === 'pedido' && o.mesa.id === mesaId,
      ),
    )

  return {
    isOnline: readonly(isOnline),
    pendingOrders,
    pendingCierres,
    pendingCount,
    isSyncing: readonly(isSyncing),
    lastSyncError: readonly(lastSyncError),
    enqueueOrder,
    enqueueCierre,
    removeFromQueue,
    retryNow,
    pendingForMesa,
  }
}

// isNetworkError se auto-importa desde utils/network.ts por Nuxt
